import React from 'react';
import { FileSpreadsheet, Wallet } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import PayrollDocSettingsFields from '@/components/payroll/PayrollDocSettingsFields';
import { accountingApi } from '@/lib/api/accounting';
import { flattenHeadings, type HeadingFlat } from '@/document-entry/headingCatalog';
import { formatRial } from '@/lib/formatMoney';
import {
  isPayrollDocSettingsValid,
  readPayrollDocSettings,
  savePayrollDocSettings,
  type PayrollDocSettings,
} from '@/lib/payroll/payrollDocSettings';

export type PayrollAccrualModalSummary = {
  monthLabel: string;
  dateRangeLabel?: string;
  employeeCount: number;
  projectDebitLines: number;
  totalNet: number;
  creditTotal: number;
  debitTotal: number;
};

type PayrollAccrualSettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: PayrollAccrualModalSummary | null;
  onConfirm: (settings: PayrollDocSettings) => void;
};

const PayrollAccrualSettingsModal: React.FC<PayrollAccrualSettingsModalProps> = ({
  open,
  onOpenChange,
  summary,
  onConfirm,
}) => {
  const [settings, setSettings] = React.useState<PayrollDocSettings>(() => readPayrollDocSettings());
  const [headingCatalog, setHeadingCatalog] = React.useState<HeadingFlat[]>([]);
  const [headingsLoading, setHeadingsLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setSettings(readPayrollDocSettings());
    setError('');
    setHeadingsLoading(true);
    accountingApi
      .accountingHeadingsTree()
      .then((tree) => setHeadingCatalog(flattenHeadings(tree)))
      .catch(() => setHeadingCatalog([]))
      .finally(() => setHeadingsLoading(false));
  }, [open]);

  const patchSettings = (patch: Partial<PayrollDocSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
    setError('');
  };

  const handleConfirm = () => {
    const trimmed: PayrollDocSettings = {
      payable_account_code: settings.payable_account_code.trim(),
      expense_account_code: settings.expense_account_code.trim(),
    };
    if (!isPayrollDocSettingsValid(trimmed)) {
      setError('هر دو حساب بستانکار (بدهی حقوق) و بدهکار (هزینه) را انتخاب کنید.');
      return;
    }
    savePayrollDocSettings(trimmed);
    onConfirm(trimmed);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl [&>button]:left-4 [&>button]:right-auto">
        <DialogHeader className="space-y-2 text-right">
          <DialogTitle className="flex items-center justify-end gap-2 text-base">
            <Wallet className="h-5 w-5 text-indigo-600" />
            تنظیمات سند استقرار حقوق
          </DialogTitle>
          <DialogDescription className="text-right text-sm leading-relaxed text-slate-600">
            یک‌بار حساب بستانکار (بدهی به پرسنل) و بدهکار (هزینه پروژه) را مشخص کنید؛ همهٔ ردیف‌های سند با همین
            کدها ساخته می‌شوند.
          </DialogDescription>
        </DialogHeader>

        {summary ? (
          <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 px-4 py-3 text-sm text-slate-800">
            <div className="font-medium text-indigo-950">{summary.monthLabel}</div>
            {summary.dateRangeLabel ? (
              <div className="mt-0.5 text-xs text-slate-600">{summary.dateRangeLabel}</div>
            ) : null}
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <span className="rounded-md bg-white px-2 py-1">{summary.employeeCount} بستانکار (پرسنل)</span>
              <span className="rounded-md bg-white px-2 py-1">{summary.projectDebitLines} بدهکار (پروژه)</span>
              <span className="rounded-md bg-white px-2 py-1 font-mono">جمع {formatRial(summary.totalNet)}</span>
            </div>
          </div>
        ) : null}

        {headingsLoading ? (
          <p className="text-sm text-slate-500">در حال آماده‌سازی…</p>
        ) : (
          <>
            {headingCatalog.length === 0 ? (
              <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                سرفصل حساب‌ها فعلاً حذف شده است. کد حساب را به‌صورت دستی وارد کنید.
              </p>
            ) : null}
            <PayrollDocSettingsFields
              settings={settings}
              onChange={patchSettings}
              headingCatalog={headingCatalog}
            />
          </>
        )}

        <p className="text-xs text-slate-500">
          ترتیب ردیف‌ها در سند: ابتدا <strong>بستانکار</strong> به ازای هر پرسنل، سپس <strong>بدهکار</strong> به
          تفکیک پروژه. پس از واریز، سند پرداخت جدا ثبت کنید.
        </p>

        {error ? (
          <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</p>
        ) : null}

        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button type="button" className="gap-2" onClick={handleConfirm} disabled={headingsLoading}>
            <FileSpreadsheet className="h-4 w-4" />
            ساخت سند و ادامه
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            انصراف
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PayrollAccrualSettingsModal;
